import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  BellOff, Calendar, CalendarDays, CheckCircle, ChevronDown, ChevronRight,
  Loader2, Mail, RefreshCw, RotateCw, Settings, Sparkles, XCircle,
} from 'lucide-react';
import { SettingsDialog as PlanningSettingsDialog } from '../../pages/PlanningChantiersPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


export const LoginDayGroup = ({ dayKey, logs }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`day-group-${dayKey}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <Calendar className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-semibold text-slate-700">{dayKey}</span>
        </div>
        <Badge variant="secondary" className="text-xs">{logs.length} connexion{logs.length > 1 ? 's' : ''}</Badge>
      </button>
      {open && (
        <div className="divide-y divide-slate-100">
          {logs.map((log, idx) => (
            <div key={log.id || idx} className="flex items-center justify-between px-4 py-2 text-sm">
              <span className="font-mono text-blue-600 font-medium">
                {format(new Date(log.timestamp), 'HH:mm:ss')}
              </span>
              <span className="text-slate-400 text-xs">{log.ip_address}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Diagnostic Panel Component
export const DiagnosticPanel = ({ api, language }) => {
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const runDiagnostic = async () => {
    setLoading(true);
    try {
      const response = await api().get('/debug/email-test');
      setDiagnosticResult(response.data);
    } catch (error) {
      setDiagnosticResult({
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Configuration Test */}
      <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          {language === 'fr' ? 'Configuration Email (Resend)' : 'Email Configuration (Resend)'}
        </h3>
        
        <Button onClick={runDiagnostic} disabled={loading} className="mb-4">
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Test en cours...</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-2" /> Tester la configuration email</>
          )}
        </Button>

        {diagnosticResult && (
          <div className="space-y-4">
            {diagnosticResult.error ? (
              <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 font-semibold">
                  <XCircle className="w-5 h-5" />
                  Erreur
                </div>
                <p className="text-red-600 mt-2">{diagnosticResult.error}</p>
              </div>
            ) : (
              <>
                {/* Config */}
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border">
                  <h4 className="font-medium mb-3">Configuration</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      {diagnosticResult.config?.resend_available ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span>Resend disponible</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {diagnosticResult.config?.resend_api_key_set ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span>Clé API configurée</span>
                    </div>
                    <div className="col-span-2 mt-2">
                      <span className="text-slate-500">Clé API: </span>
                      <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs">
                        {diagnosticResult.config?.resend_api_key_prefix || 'Non définie'}
                      </code>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-500">Email expéditeur: </span>
                      <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs">
                        {diagnosticResult.config?.sender_email || 'Non défini'}
                      </code>
                    </div>
                  </div>
                </div>

                {/* Test Result */}
                <div className={`rounded-lg p-4 border ${
                  diagnosticResult.test_result?.status === 'success' 
                    ? 'bg-green-50 border-green-300' 
                    : 'bg-red-50 border-red-300'
                }`}>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    {diagnosticResult.test_result?.status === 'success' ? (
                      <><CheckCircle className="w-5 h-5 text-green-600" /> Test d'envoi réussi</>
                    ) : (
                      <><XCircle className="w-5 h-5 text-red-600" /> Test d'envoi échoué</>
                    )}
                  </h4>
                  {diagnosticResult.test_result?.status === 'success' ? (
                    <p className="text-green-700 text-sm">
                      Un email de test a été envoyé avec succès. Vérifiez votre boîte de réception.
                    </p>
                  ) : (
                    <p className="text-red-700 text-sm">
                      {diagnosticResult.test_result?.error || 'Erreur inconnue'}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* System Info */}
      <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-slate-600" />
          {language === 'fr' ? 'Informations Système' : 'System Information'}
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Frontend URL: </span>
            <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs break-all">
              {window.location.origin}
            </code>
          </div>
          <div>
            <span className="text-slate-500">Backend URL: </span>
            <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs break-all">
              {BACKEND_URL}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// ============================================================================
// Planning Chantiers — Admin tab content
export function EmailPausePanel({ api, language }) {
  const [setting, setSetting] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [availableCategories, setAvailableCategories] = useState({});
  const [categories, setCategories] = useState({});
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const loadReport = async () => {
    setLoadingReport(true);
    try {
      const r = await api().get('/admin/email-pause/suppressed');
      setReport(r.data);
    } catch (e) { /* ignore */ }
    finally { setLoadingReport(false); }
  };

  const applyData = (d) => {
    setSetting(d);
    setEnabled(!!d.enabled);
    setFrom(d.date_from || '');
    setUntil(d.date_until || '');
    setAvailableCategories(d.available_categories || {});
    setCategories(d.categories || {});
  };

  const load = async () => {
    try {
      const r = await api().get('/admin/email-pause');
      applyData(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); loadReport(); /* eslint-disable-next-line */ }, []);

  const save = async (nextEnabled, nextCategories) => {
    setSaving(true);
    try {
      const cats = nextCategories || categories;
      const r = await api().put('/admin/email-pause', {
        enabled: nextEnabled,
        date_from: from || null,
        date_until: until || null,
        categories: cats,
      });
      applyData(r.data);
      toast.success(nextEnabled ? 'Notifications email mises en pause' : 'Notifications email réactivées');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const toggleCategory = async (key, value) => {
    const next = { ...categories, [key]: value };
    setCategories(next);
    await save(enabled, next);
  };

  const activeNow = setting?.active_now;
  const catKeys = Object.keys(availableCategories);
  return (
    <Card className={`mb-4 border ${activeNow ? 'border-amber-300 bg-amber-50/60 dark:bg-amber-900/10' : 'border-slate-200 dark:border-slate-800'}`} data-testid="email-pause-panel">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2 text-base">
          <BellOff className="w-5 h-5 text-amber-600" />
          Pause des notifications email
        </CardTitle>
        <CardDescription>
          Suspend les <b>emails d'information/notification</b> (alertes, relances, rappels automatiques, annonces, mises en copie admin…). Les <b>envois manuels</b> (tournée d'un commercial, envoi d'un contrat, envoi à HG…) restent toujours actifs. Idéal pendant une période de congés.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {activeNow && (
          <div className="text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 rounded-lg px-3 py-2" data-testid="email-pause-active-banner">
            ⏸️ Les notifications email sont actuellement <b>en pause</b>{setting?.date_until ? ` jusqu'au ${new Date(setting.date_until).toLocaleDateString('fr-FR')}` : ''}.
          </div>
        )}
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-100 dark:border-slate-800 p-3">
          <div>
            <p className="text-sm font-medium">Activer la pause des emails</p>
            <p className="text-xs text-slate-500">Si aucune date n'est renseignée, la pause reste active jusqu'à désactivation manuelle.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={(v) => save(v)} disabled={saving} data-testid="email-pause-toggle" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Du (optionnel)</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" data-testid="email-pause-from" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Au (optionnel)</label>
            <input type="date" value={until} onChange={e => setUntil(e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" data-testid="email-pause-until" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => save(enabled)} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="email-pause-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer la période
          </Button>
          {setting?.updated_by && <span className="text-[11px] text-slate-400">Modifié par {setting.updated_by}</span>}
        </div>

        {catKeys.length > 0 && (
          <div className={`rounded-lg border border-slate-100 dark:border-slate-800 p-3 ${enabled ? '' : 'opacity-60'}`} data-testid="email-pause-categories">
            <p className="text-sm font-medium flex items-center gap-2">
              Gestion au cas par cas
              {!enabled && <span className="text-[11px] font-normal text-slate-400">(activez la pause pour appliquer)</span>}
            </p>
            <p className="text-xs text-slate-500 mb-3">Décochez une catégorie pour continuer à l'envoyer même pendant la pause.</p>
            <div className="grid grid-cols-1 gap-2">
              {catKeys.map((key) => (
                <label key={key} className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer" data-testid={`email-pause-cat-row-${key}`}>
                  <span className="text-sm text-slate-700 dark:text-slate-200">{availableCategories[key]}</span>
                  <Switch
                    checked={categories[key] !== false}
                    onCheckedChange={(v) => toggleCategory(key, v)}
                    disabled={saving || !enabled}
                    data-testid={`email-pause-cat-${key}`}
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-lg border border-slate-100 dark:border-slate-800 p-3" data-testid="email-pause-report">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-sm font-medium">Rapport de pause</p>
            <Button size="sm" variant="outline" onClick={loadReport} disabled={loadingReport} data-testid="email-pause-report-refresh">
              {loadingReport ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
              <span className="ml-1 text-xs">Actualiser</span>
            </Button>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Emails automatiques <b>non envoyés</b> pendant les pauses. Un <b>Résumé de reprise</b> est envoyé automatiquement aux admins à la fin de chaque pause.
            {report?.last_recap_sent_at && (
              <span className="block text-[11px] text-slate-400 mt-1">Dernier résumé de reprise envoyé le {new Date(report.last_recap_sent_at).toLocaleString('fr-FR')}.</span>
            )}
          </p>
          {(!report || report.total === 0) ? (
            <p className="text-sm text-slate-500" data-testid="email-pause-report-empty">Aucune notification suspendue pour l'instant.</p>
          ) : (
            <div className="space-y-3" data-testid="email-pause-report-content">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" data-testid="email-pause-report-total">{report.total} suspendu(s)</Badge>
                {Object.entries(report.by_category || {}).map(([lbl, n]) => (
                  <Badge key={lbl} variant="outline" className="text-[11px]">{lbl} · {n}</Badge>
                ))}
              </div>
              <div className="max-h-64 overflow-auto rounded-md border border-slate-100 dark:border-slate-800">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium text-slate-500">Date</th>
                      <th className="text-left px-2 py-1.5 font-medium text-slate-500">Catégorie</th>
                      <th className="text-left px-2 py-1.5 font-medium text-slate-500">Destinataire</th>
                      <th className="text-left px-2 py-1.5 font-medium text-slate-500">Objet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.items || []).map((it) => (
                      <tr key={it.id} className="border-t border-slate-100 dark:border-slate-800" data-testid="email-pause-report-row">
                        <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{it.suppressed_at ? new Date(it.suppressed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td className="px-2 py-1.5 text-slate-700 dark:text-slate-200">{it.category_label || it.category}</td>
                        <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">{it.recipient || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">{it.subject || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


export function PlanningChantiersAdminTab({ token, language }) {
  const [showSettings, setShowSettings] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleRegenerate = async () => {
    if (!window.confirm('Régénérer un planning démo ? Cela remplacera tous les chantiers générés et effacera les ajustements manuels.')) return;
    setRegenerating(true);
    try {
      const res = await fetch(`${API}/api/planning-chantiers/generate-demo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('regen');
      toast.success('Planning démo régénéré');
    } catch { toast.error('Erreur de régénération'); }
    finally { setRegenerating(false); }
  };

  return (
    <Card className="border border-slate-200 dark:border-slate-800">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-amber-500" />
          {language === 'fr' ? 'Planning chantiers — Ouvertures' : 'Construction Planning'}
        </CardTitle>
        <CardDescription>
          {language === 'fr'
            ? 'Gestion du planning rolling 14 semaines : régions, statuts, génération démo.'
            : 'Manage the rolling 14-week planning: regions, statuses, demo generation.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4 space-y-2">
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" /> Générer un planning démo
                </p>
                <p className="text-xs text-slate-600">
                  Re-shuffle complet les régions, types et statuts sur les 14 semaines (les ajustements manuels seront effacés).
                </p>
                <Button onClick={handleRegenerate} disabled={regenerating}
                  className="bg-amber-500 hover:bg-amber-600 text-white gap-1"
                  data-testid="generate-demo-btn">
                  {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Générer maintenant
                </Button>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 space-y-2">
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-slate-500" /> Paramètres
                </p>
                <p className="text-xs text-slate-600">
                  Modifier les régions disponibles, les statuts, le nombre de semaines et les ratios de génération.
                </p>
                <Button variant="outline" onClick={() => setShowSettings(true)} className="gap-1"
                  data-testid="open-planning-settings">
                  <Settings className="w-4 h-4" /> Ouvrir
                </Button>
              </CardContent>
            </Card>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <p className="font-semibold mb-1">Astuce</p>
            <p>Pour modifier ou désactiver une semaine spécifique, rends-toi sur la page <code className="bg-white px-1 rounded">Planning chantiers</code>{' '}
              dans le menu latéral. Les boutons <code className="bg-white px-1 rounded">Modifier</code> et <code className="bg-white px-1 rounded">Réinitialiser</code> apparaissent sur chaque carte de semaine en mode admin.</p>
          </div>
        </div>
      </CardContent>
      {showSettings && (
        <PlanningSettingsDialog
          open={showSettings}
          onClose={() => setShowSettings(false)}
          token={token}
          onSaved={() => { /* page reloads on its own */ }}
        />
      )}
    </Card>
  );
}
